/**
 * MeetingNotesDetailSection — Type-specific section for ahoy_meeting_notes.
 *
 * Renders the summary, action items, and key topics from the meeting notes
 * entity, using the same compact markdown rendering as MeetingNotesCard.
 */

import { useMemo } from 'react';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';

import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';
// Imports split between two modules to break a circular dependency.
// `entityRendering` pulls in EntityBadgeById, which transitively imports
// the entity-type registry, which imports this file (registered as
// `ahoy_meeting_notes.summary`). Routing the cycle-free symbols
// (preprocessEntityTags + entitySanitizeSchema) through `entitySanitize`
// keeps that loop from closing during module init. See entitySanitize.ts
// for the full explanation.
import { createEntityMarkdownComponents } from '@/lib/markdown/entityRendering';
import {
  entitySanitizeSchema,
  preprocessEntityTags,
} from '@/lib/markdown/entitySanitize';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import {
  enrichWithSpeakerMap,
  parseSpeakerMap,
} from '../utils/meetingEnrichment';
import { CollapsibleSection } from './collapsible-section';

// ============================================================================
// Static config (hoisted to avoid new references on every render)
// ============================================================================

const REHYPE_PLUGINS = [
  rehypeRaw,
  [rehypeSanitize, entitySanitizeSchema],
] as unknown[];

// ============================================================================
// Component
// ============================================================================

interface MeetingNotesDetailSectionProps {
  object: CrmObject;
}

export function MeetingNotesDetailSection({
  object,
}: MeetingNotesDetailSectionProps) {
  const orgId = object.org_id;

  const summary = getPropertyValue<string>(object.properties?.ahoy_summary);
  const actionItems = getPropertyValue<string>(
    object.properties?.ahoy_action_items
  );
  const keyTopics = getPropertyValue<string>(
    object.properties?.ahoy_key_topics
  );

  const speakerMap = useMemo(
    () =>
      parseSpeakerMap(
        getPropertyValue<string>(object.properties?.ahoy_speaker_map)
      ),
    [object.properties?.ahoy_speaker_map]
  );

  const markdownComponents = useMemo(
    () => createEntityMarkdownComponents(orgId),
    [orgId]
  );

  const hasContent = summary || actionItems || keyTopics;

  if (!hasContent) {
    return null;
  }

  return (
    <>
      {summary && (
        <CollapsibleSection title='Meeting summary' defaultExpanded>
          <MarkdownContent
            content={preprocessEntityTags(
              enrichWithSpeakerMap(summary, speakerMap)
            )}
            rehypePlugins={REHYPE_PLUGINS}
            components={markdownComponents}
            size='compact'
            className='text-xs md:text-sm'
          />
        </CollapsibleSection>
      )}

      {actionItems && (
        <CollapsibleSection title='Action items' defaultExpanded>
          <MarkdownContent
            content={preprocessEntityTags(
              enrichWithSpeakerMap(actionItems, speakerMap)
            )}
            rehypePlugins={REHYPE_PLUGINS}
            components={markdownComponents}
            size='compact'
            className='text-xs md:text-sm'
          />
        </CollapsibleSection>
      )}

      {keyTopics && (
        <CollapsibleSection title='Key topics' defaultExpanded>
          <MarkdownContent
            content={preprocessEntityTags(
              enrichWithSpeakerMap(keyTopics, speakerMap)
            )}
            rehypePlugins={REHYPE_PLUGINS}
            components={markdownComponents}
            size='compact'
            className='text-xs md:text-sm'
          />
        </CollapsibleSection>
      )}
    </>
  );
}
